// ShiftOrderingControls.jsx
// The live "🎛 Shift Controls" panel body — hosted by ShiftManager in a
// modal during an active shift. Two sections:
//
//   1. On-screen ordering — writes the shift.qrFlows model (utils/qrFlow.js):
//      mode picker (Off / Party / Market / Custom link), custom URL, per-scene
//      overrides, summon escalation toggle. Every write goes through
//      qrFlowsWritePatch() so the legacy livestreamJoinEnabled gate (and the
//      /join server check) stays mirrored.
//   2. Scene context — the stream/booth/both selector that used to be its own
//      cycling HUD button.
//
// Overlays consume via onSnapshot, so every tap here is live on the screens
// within a frame. Touch targets sized for the iPad (artist device).

import { useState } from "react";
import {
  QR_FLOW_IDS,
  QR_FLOW_META,
  QR_SCENES,
  qrFlowsWritePatch,
} from "./../utils/qrFlow";
import { STAFF_THEME } from "./../theme/staff";
import { COMBOS, COMBO_ORDER, fmtCents } from "../utils/combos";
import { shiftPricing } from "../utils/eventPricing";

const BRAND = STAFF_THEME;

const SCENE_LABELS = {
  booth: "Booth",
  break: "Break",
  startingSoon: "Starting Soon",
  main: "Main",
};

const FLOW_TINT = {
  off: BRAND.dim,
  free: BRAND.pink,
  paid: BRAND.gold,
  custom: BRAND.sky,
};

const sectionTitle = {
  color: "white",
  fontSize: "0.95rem",
  fontWeight: 700,
};
const sectionHint = {
  color: BRAND.dim,
  fontSize: "0.78rem",
  lineHeight: 1.4,
  marginTop: "-0.35rem",
};

export default function ShiftOrderingControls({ shift, contextMode, onWrite, onContextMode }) {
  const qf = shift.qrFlows || null;
  const mode = qf?.mode || null; // null = legacy toggles still driving
  const [urlDraft, setUrlDraft] = useState(qf?.customUrl || "");

  function write(nextQf) {
    onWrite(qrFlowsWritePatch(nextQf));
  }

  function base() {
    return {
      mode: qf?.mode || (shift.livestreamJoinEnabled ? "paid" : "off"),
      customUrl: qf?.customUrl ?? null,
      scenes: qf?.scenes ?? {},
      summonEnabled: qf?.summonEnabled !== false,
    };
  }

  function pickMode(m) {
    write({ ...base(), mode: m });
  }

  function saveUrl() {
    const trimmed = urlDraft.trim();
    write({ ...base(), customUrl: trimmed || null });
  }

  function cycleScene(scene) {
    // Default → Off → each flow → back to Default. Skips the shift's own
    // mode (that IS default) so the cycle never shows a redundant stop.
    const order = ["default", "off", ...QR_FLOW_IDS.filter((f) => f !== "off")];
    const cur = qf?.scenes?.[scene] || "default";
    let next = order[(order.indexOf(cur) + 1) % order.length];
    if (next === base().mode) next = order[(order.indexOf(next) + 1) % order.length];
    const scenes = { ...(qf?.scenes || {}) };
    if (next === "default") delete scenes[scene];
    else scenes[scene] = next;
    write({ ...base(), scenes });
  }

  const sceneChipLabel = (scene) => {
    const v = qf?.scenes?.[scene] || "default";
    if (v === "default") return "Default";
    if (v === "off") return "Off";
    const meta = QR_FLOW_META[v];
    return meta ? `${meta.icon} ${meta.label}` : v;
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.8rem" }}>
      {/* ── 1. ON-SCREEN ORDERING ─────────────────────────────────────── */}
      <div style={sectionTitle}>📱 On-screen ordering</div>
      <div style={sectionHint}>
        One QR story per screen. Changes hit the overlays live.
      </div>

      {!mode && (
        <div style={{
          background: "rgba(252,179,31,0.08)",
          border: `1px solid ${BRAND.gold}`,
          borderRadius: "8px", padding: "0.5rem 0.65rem",
          color: BRAND.gold, fontSize: "0.78rem", lineHeight: 1.4,
        }}>
          This shift is still on the old “📲 QR Ordering” toggle
          ({shift.livestreamJoinEnabled ? "ON" : "OFF"}). Pick a mode below to
          switch it to the flow system.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
        {QR_FLOW_IDS.map((id) => {
          const meta = QR_FLOW_META[id];
          const active = mode === id;
          const tint = FLOW_TINT[id];
          return (
            <button
              key={id}
              onClick={() => pickMode(id)}
              style={{
                textAlign: "left", cursor: "pointer",
                background: active ? "rgba(255,255,255,0.07)" : BRAND.panel,
                border: `2px solid ${active ? tint : BRAND.border}`,
                borderRadius: "10px",
                padding: "0.6rem 0.7rem",
                minHeight: "72px",
              }}
            >
              <div style={{ color: active ? tint : "white", fontSize: "0.9rem", fontWeight: 700 }}>
                {meta.icon} {meta.label}
              </div>
              <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.35, marginTop: "0.25rem" }}>
                {meta.blurb}
              </div>
            </button>
          );
        })}
      </div>

      {mode === "custom" && (
        <div>
          <div style={{ color: BRAND.muted, fontSize: "0.75rem", marginBottom: "0.3rem" }}>
            QR target URL (falls back to Stream Setup's QR URL when blank)
          </div>
          <input
            value={urlDraft}
            onChange={(e) => setUrlDraft(e.target.value)}
            onBlur={saveUrl}
            placeholder="https://…"
            style={{
              width: "100%", boxSizing: "border-box",
              background: "rgba(255,255,255,0.06)",
              border: `1px solid ${BRAND.border}`,
              borderRadius: "8px", color: "white",
              fontSize: "0.9rem", padding: "0.6rem 0.7rem", outline: "none",
            }}
          />
        </div>
      )}

      {mode && (
        <>
          {/* Per-scene overrides — tap a chip to cycle */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
            {QR_SCENES.map((scene) => {
              const v = qf?.scenes?.[scene] || "default";
              const overridden = v !== "default";
              return (
                <div key={scene} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: "0.6rem",
                  background: BRAND.panel,
                  border: `1px solid ${overridden ? BRAND.sky : BRAND.border}`,
                  borderRadius: "8px", padding: "0.4rem 0.65rem",
                }}>
                  <div style={{ color: overridden ? "white" : BRAND.muted, fontSize: "0.85rem", fontWeight: 600 }}>
                    {SCENE_LABELS[scene]}
                  </div>
                  <button
                    onClick={() => cycleScene(scene)}
                    style={{
                      background: "rgba(255,255,255,0.05)",
                      border: `1px solid ${overridden ? BRAND.sky : BRAND.border}`,
                      borderRadius: "999px",
                      color: overridden ? BRAND.sky : BRAND.dim,
                      fontSize: "0.78rem", fontWeight: 700,
                      padding: "0.4rem 0.9rem", minWidth: "110px",
                      cursor: "pointer",
                    }}
                  >
                    {sceneChipLabel(scene)}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Summon escalation */}
          <label style={{
            display: "flex", alignItems: "center", gap: "0.6rem",
            background: BRAND.panel,
            border: `1px solid ${qf?.summonEnabled !== false ? BRAND.gold : BRAND.border}`,
            borderRadius: "8px", padding: "0.5rem 0.65rem",
            cursor: "pointer", userSelect: "none",
          }}>
            <input
              type="checkbox"
              checked={qf?.summonEnabled !== false}
              onChange={(e) => write({ ...base(), summonEnabled: e.target.checked })}
              style={{ width: "20px", height: "20px", flexShrink: 0, accentColor: BRAND.gold }}
            />
            <div>
              <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 700 }}>
                ✨ Summon escalation
              </div>
              <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.35 }}>
                Empty line → big banner · 1–6 waiting → small tile · 7+ → silent.
                Off = a steady small QR instead.
              </div>
            </div>
          </label>
        </>
      )}

      {/* ── COMBO / GOODS OFFERS ──────────────────────────────────────── */}
      {/* Market voice: pick which full combos this shift upsells. Free/party
          voice: commerce stays fully suppressed UNLESS the operator flips
          "Sell take-home goods" — the door-paid-show format (pancakes free,
          kits/prints sold at goods-only prices via the portal + spotlight). */}
      <div style={{ height: "1px", background: BRAND.divider, margin: "0.1rem 0" }} />
      <div style={sectionTitle}>🎁 {shift?.qrFlows?.mode === "free" ? "Take-home goods" : "Combo offers"}</div>
      {shift?.qrFlows?.mode === "free" && (
        <label style={{
          display: "flex", alignItems: "center", gap: "0.6rem",
          background: BRAND.panel,
          border: `1px solid ${shift?.goodsMode === "sell" ? BRAND.gold : BRAND.border}`,
          borderRadius: "8px", padding: "0.5rem 0.65rem",
          cursor: "pointer", userSelect: "none",
        }}>
          <input
            type="checkbox"
            checked={shift?.goodsMode === "sell"}
            onChange={(e) => onWrite({ goodsMode: e.target.checked ? "sell" : "none" })}
            style={{ width: "20px", height: "20px", flexShrink: 0, accentColor: BRAND.gold }}
          />
          <div>
            <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 700 }}>
              Sell take-home goods on free pancakes
            </div>
            <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.35 }}>
              Guests' order pages offer Kit $15 / Kit + Print $25; the spotlight
              advertises them. Pancakes stay free — off = zero commerce (default).
            </div>
          </div>
        </label>
      )}
      {(shift?.qrFlows?.mode !== "free" || shift?.goodsMode === "sell") && (
        <>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            {COMBO_ORDER.map((key) => {
              const combo = COMBOS[key];
              const activeKeys = Array.isArray(shift?.activeComboKeys) ? shift.activeComboKeys : [];
              const active = activeKeys.includes(key);
              return (
                <button
                  key={key}
                  onClick={() => onWrite({
                    activeComboKeys: active ? activeKeys.filter((k) => k !== key) : [...activeKeys, key],
                  })}
                  style={{
                    flex: 1, cursor: "pointer",
                    background: active ? "rgba(252,179,31,0.12)" : BRAND.panel,
                    border: `2px solid ${active ? BRAND.gold : BRAND.border}`,
                    borderRadius: "10px",
                    color: active ? BRAND.gold : BRAND.dim,
                    fontSize: "0.8rem", fontWeight: 700,
                    padding: "0.6rem 0.4rem",
                  }}
                >
                  {combo.emoji} {combo.label}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ── ROAD MODE PRICING ─────────────────────────────────────────── */}
      {/* Read-mostly: the Event Config profile is the editor. The one live
          action is removing the block mid-shift (instant revert to home
          prices) — Square links already minted stay valid at their price;
          new taps mint fresh links (priced-intent link reuse). */}
      {(() => {
        const p = shiftPricing(shift);
        if (!p) return null;
        return (
          <>
            <div style={{ height: "1px", background: BRAND.divider, margin: "0.1rem 0" }} />
            <div style={sectionTitle}>🧳 Event pricing{p.eventTag ? ` — ${p.eventTag}` : ""}</div>
            <div style={{
              background: "rgba(252,179,31,0.08)", border: `1px solid ${BRAND.gold}`,
              borderRadius: "8px", padding: "0.5rem 0.65rem",
              color: BRAND.muted, fontSize: "0.74rem", lineHeight: 1.5,
            }}>
              ⚡ {fmtCents(p.flashCents ?? 1000)} · 📷 {fmtCents(p.customCents ?? 3000)}
              {p.extras.map((e) => ` · ${e.emoji} +${fmtCents(e.cents)}`).join("")}
              {p.taxPct ? ` · +${p.taxPct}% tax` : ""} · tipping on
              {p.hideStandby ? " · standby hidden" : ""}
              <br />Reverts automatically at shift close.
            </div>
            <button
              onClick={() => {
                if (window.confirm("Remove event pricing for the rest of this shift? New orders go back to home prices immediately.")) {
                  onWrite({ pricing: null });
                }
              }}
              style={{
                alignSelf: "flex-start", background: "none",
                border: `1px solid ${BRAND.border}`, borderRadius: "8px",
                color: BRAND.dim, fontSize: "0.74rem", fontWeight: 600,
                padding: "0.35rem 0.7rem", cursor: "pointer",
              }}
            >
              ✕ Back to home prices now
            </button>
          </>
        );
      })()}

      {/* ── BLUESKY GRIDDLE BOT ───────────────────────────────────────── */}
      <div style={{ height: "1px", background: BRAND.divider, margin: "0.1rem 0" }} />
      <div style={sectionTitle}>🦋 Bluesky live thread</div>
      <label style={{
        display: "flex", alignItems: "center", gap: "0.6rem",
        background: BRAND.panel,
        border: `1px solid ${shift.blueskyAutoPost ? BRAND.sky : BRAND.border}`,
        borderRadius: "8px", padding: "0.5rem 0.65rem",
        cursor: "pointer", userSelect: "none",
      }}>
        <input
          type="checkbox"
          checked={!!shift.blueskyAutoPost}
          onChange={(e) => onWrite({ blueskyAutoPost: e.target.checked })}
          style={{ width: "20px", height: "20px", flexShrink: 0, accentColor: BRAND.sky }}
        />
        <div>
          <div style={{ color: "white", fontSize: "0.85rem", fontWeight: 700 }}>
            Auto-post pancakes to Bluesky
          </div>
          <div style={{ color: BRAND.dim, fontSize: "0.7rem", lineHeight: 1.35 }}>
            Each finished pancake posts into one live shift thread on
            @thejoyofpancakes.com; shift close adds the recap. Off = silent.
          </div>
        </div>
      </label>

      {/* ── 2. SCENE CONTEXT ──────────────────────────────────────────── */}
      <div style={{ height: "1px", background: BRAND.divider, margin: "0.1rem 0" }} />
      <div style={sectionTitle}>🎬 Scene context</div>
      <div style={sectionHint}>
        Which audience today's overlays talk to (header style + marquee filter).
      </div>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        {[
          { id: "stream", label: "📺 Stream" },
          { id: "booth", label: "🏪 Booth" },
          { id: "both", label: "🎪 Both" },
        ].map((c) => {
          const active = (contextMode || "stream") === c.id;
          return (
            <button
              key={c.id}
              onClick={() => onContextMode(c.id)}
              style={{
                flex: 1, cursor: "pointer",
                background: active ? "rgba(252,179,31,0.12)" : BRAND.panel,
                border: `2px solid ${active ? BRAND.gold : BRAND.border}`,
                borderRadius: "10px",
                color: active ? BRAND.gold : BRAND.dim,
                fontSize: "0.85rem", fontWeight: 700,
                padding: "0.65rem 0.4rem",
              }}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
